import { BlurredBackgroundImage } from '@/components/ui/blurred-background-image';
import { useIsMobile } from '@/hooks/use-is-mobile';
import { useProvidedTheme } from '@/hooks/use-provided-theme';
import { clamp } from '@calm/evexia-web-common';
import { animated, useScroll, useSpring } from '@react-spring/web';
import BackButton from '@rhg/components/back-button';
import { HeroBannerImage } from '@rhg/components/ui/hero-banner-image';
import {
    BANNER_HEIGHT,
    BANNER_WIDTH,
} from '@rhg/components/ui/hero-banner-image/types';
import {
    Box,
    Grid,
    GridProps,
    SxProps,
    Theme,
    ThemeProvider,
    useMediaQuery,
    useTheme,
} from '@rhg/libs/material';
import {
    MaskOrientation,
    styleMaskedContainer,
    sxAppend,
} from '@rhg/libs/material/sx';
import {
    Children,
    PropsWithChildren,
    useCallback,
    useRef,
    useState,
} from 'react';

const styleContentContainer = {
    flex: 1,
    width: '100%',
    height: {
        xs: 'auto',
        md: '100%',
    },
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    px: (theme: Theme) => ({
        sm: theme.ds.spacingUnits.SIDE_SMALL,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
    pt: (theme: Theme) => ({
        sm: 0,
        md: theme.semantics.Spacing[24],
        lg: theme.ds.spacingUnits.SIDE_LARGE,
    }),
    pb: (theme: Theme) => ({
        sm: 0,
        md: theme.ds.spacingUnits.SIDE_MEDIUM,
        lg: 0,
    }),

    mt: {
        xs: 12,
        sm: 12,
        md: 0,
    },
    '& *': {
        zIndex: 2,
    },
};
const styleContentBesideImage = {
    justifyContent: { xs: 'flex-start', lg: 'flex-end' },
};
const styleContentColumn = {
    maxWidth: (theme: Theme) => theme.breakpoints.values.md,
    width: { xs: '100%', lg: '85%', xl: '60%' },
};

const styleLeftColumn = {
    position: 'relative',
    minHeight: BANNER_HEIGHT,
};
const styleLeftColumnSm = {
    height: BANNER_HEIGHT,
    minHeight: 'auto',
};
// When the header has no content (image-only headers like the pack/feed page,
// which render their title/subtitle separately below), the content column has
// nothing to size for. On mobile the grid is column-reverse, so a banner-height
// empty column would stack a large dead space beneath the image — collapse it.
const styleLeftColumnEmpty = {
    height: 'auto',
    minHeight: 0,
};
const styleEmptyContentContainer = {
    mt: 0,
    minHeight: 0,
};
const styleBackButton = {
    position: 'absolute',
    top: (theme: Theme) => ({
        xs: theme.semantics.Spacing[16],
        md: theme.semantics.Spacing[24],
        lg: theme.semantics.Spacing[32],
    }),
    left: (theme: Theme) => ({
        xs: theme.semantics.Spacing[16],
        md: theme.semantics.Spacing[24],
        lg: theme.semantics.Spacing[32],
    }),
};

const styleBackground = {
    height: '100%',
    position: 'absolute',
    left: 0,
    top: 0,
    width: BANNER_WIDTH,
    minHeight: BANNER_HEIGHT,
};

const styleBackgroundBannerImage = {
    width: BANNER_WIDTH,
    minHeight: BANNER_HEIGHT,
};

const styleRightBannerImageContainer = {
    height: '100%',
    position: { xs: 'static', sm: 'static', md: 'absolute', lg: 'static' },
    top: { xs: undefined, sm: undefined, md: 0, lg: undefined },
    right: { xs: undefined, sm: undefined, md: 0, lg: undefined },
    '& > *': {
        height: '100%',
    },
};
const styleDarkContentColor = { color: 'text.primary' };

const styleMaxWidthGrid = {
    flexWrap: 'nowrap',
    position: 'relative',
    '> .MuiGrid-item': {
        maxWidth: { xs: '100%', md: '50%' },
    },
};

/**
 * Forces its subtree into dark mode. Wrapping the whole header (back button +
 * children + the masked hero) — rather than just the text — also flips the
 * BlurredBackgroundImage mask dark, so the light overlay text stays readable
 * regardless of the active theme. Isolated as its own component so the dark
 * theme is only built when dark mode is actually being forced.
 */
function ForceDarkContent({ children }: PropsWithChildren) {
    const darkTheme = useProvidedTheme('dark');
    return (
        <ThemeProvider theme={darkTheme}>
            {/* Cascade the dark text colour so children that merely inherit (rather
                than set an explicit palette token) also flip — e.g. the plain
                Typography titles/descriptions, which otherwise keep the ambient
                inherited colour even under the dark theme context. */}
            <Box sx={styleDarkContentColor}>{children}</Box>
        </ThemeProvider>
    );
}

function HeroHeaderWrapperContainer({
    src,
    children,
}: PropsWithChildren<{ src?: string }>) {
    const isMobile = useIsMobile();
    if (isMobile || !src) {
        return children;
    }
    return (
        <BlurredBackgroundImage
            maskedProportion={0.5}
            maskedStartProportion={0}
            orientation={MaskOrientation.Left}
            src={src}
        >
            {children}
        </BlurredBackgroundImage>
    );
}

interface HeroHeaderWrapperProps {
    heroImageSrc?: string;
    contentMdSize?: GridProps['md'];
    hideImageMask?: boolean;
    withScrollEffect?: boolean;
    sxGridContainer?: SxProps;
    sxContentContainer?: SxProps;
    fallbackBackRoute?: string;
    hideBackButton?: boolean;
    /**
     * Force the front-of-hero content (back button + children) into dark mode,
     * matching the home banner treatment. Applies only on desktop with a hero
     * image — the mobile layout stacks the image above the content on the page
     * background, where there is nothing in front of the hero to darken.
     */
    forceDarkContent?: boolean;
    /**
     * Narrow the content and push it against the hero image on desktop
     * (`lg` and up), instead of filling the column from its left edge.
     * The content is capped at the `md` breakpoint width at every size.
     */
    alignContentToImage?: boolean;
}

export function HeroHeaderWrapper({
    heroImageSrc,
    contentMdSize = 12,
    withScrollEffect = true,
    children,
    sxGridContainer,
    sxContentContainer,
    fallbackBackRoute,
    hideImageMask = false,
    hideBackButton = false,
    forceDarkContent = false,
    alignContentToImage = false,
}: PropsWithChildren<HeroHeaderWrapperProps>) {
    const theme = useTheme();
    const isMobile = useIsMobile();
    const isSmOrXs = useMediaQuery(theme.breakpoints.down('md'));
    const hasContent = Children.toArray(children).length > 0;
    const [ratioBannerShowing, setRatioBannerShowing] = useState(1);
    const bannerImageRef = useRef<Element>(null);
    const style = useSpring({
        opacity: Math.pow(ratioBannerShowing, 2),
    });

    const AnimatedHeroBannerImage = animated(HeroBannerImage);

    const handleScroll = useCallback(
        (scrollY: number) => {
            const bannerRect = bannerImageRef.current?.getBoundingClientRect();
            const pixelsShowing = (bannerRect?.bottom ?? 0) - (scrollY ?? 0);
            const ratioShowing = clamp(
                pixelsShowing / (bannerRect?.height ?? 1),
                0,
                1
            );
            setRatioBannerShowing(withScrollEffect ? ratioShowing : 1);
        },
        [withScrollEffect]
    );

    useScroll({
        onChange: ({ value: { scrollY } }: { value: { scrollY: number } }) =>
            handleScroll(scrollY),
    });

    // Only force dark where a hero is actually behind the content: desktop with
    // an image. On mobile the image stacks above the content (column-reverse),
    // so the content sits on the page background and keeps the active theme.
    const shouldForceDarkContent =
        forceDarkContent && !isMobile && Boolean(heroImageSrc);

    const header = (
        <HeroHeaderWrapperContainer src={heroImageSrc || ''}>
            <Box sx={{ position: 'relative' }}>
                {!hideBackButton && (
                    // Keep the back button on the app theme. The rest of the
                    // header may be force-dark over the hero, but the back button
                    // should stay consistent with the active theme — re-provide
                    // the outer theme to opt it out of the dark context.
                    <ThemeProvider theme={theme}>
                        <BackButton
                            sx={styleBackButton}
                            fallbackRoute={fallbackBackRoute}
                        />
                    </ThemeProvider>
                )}
                <Grid
                    container
                    direction={isSmOrXs ? 'column-reverse' : 'row'}
                    sx={sxAppend(styleMaxWidthGrid, sxGridContainer)}
                >
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={contentMdSize}
                        lg={6}
                        sx={sxAppend(
                            styleLeftColumn,
                            isSmOrXs && styleLeftColumnSm,
                            isSmOrXs && !hasContent && styleLeftColumnEmpty
                        )}
                    >
                        <Box
                            sx={sxAppend(
                                styleContentContainer,
                                isSmOrXs &&
                                    !hasContent &&
                                    styleEmptyContentContainer,
                                alignContentToImage && styleContentBesideImage,
                                sxContentContainer
                            )}
                        >
                            {alignContentToImage ? (
                                <Box
                                    sx={styleContentColumn}
                                    data-testid="HeroHeaderContentColumn"
                                >
                                    {children}
                                </Box>
                            ) : (
                                children
                            )}
                        </Box>
                    </Grid>
                    <Grid
                        item
                        sm={12}
                        xs={12}
                        md={9}
                        lg={6}
                        sx={styleRightBannerImageContainer}
                    >
                        <AnimatedHeroBannerImage
                            style={style}
                            heroImageSrc={heroImageSrc}
                            sxBannerImage={sxAppend(
                                !isSmOrXs &&
                                    !hideImageMask &&
                                    styleMaskedContainer(MaskOrientation.Right)
                            )}
                            ref={bannerImageRef}
                        />
                    </Grid>
                </Grid>
                {!isSmOrXs && hideImageMask && (
                    <HeroBannerImage
                        sx={styleBackground}
                        heroImageSrc={heroImageSrc}
                        sxBannerImage={styleBackgroundBannerImage}
                    />
                )}
            </Box>
        </HeroHeaderWrapperContainer>
    );

    return shouldForceDarkContent ? (
        <ForceDarkContent>{header}</ForceDarkContent>
    ) : (
        header
    );
}
